// Profile- and role-aware training recommendations. Replaces flat
// "average < 70%" logic: a module is only recommended if it belongs to the
// user's selected service profile, and priority is driven by the gap to the
// selected role's actual published/inferred requirement where one exists —
// never by an invented universal cutoff.
import { getServiceProfileModules } from '../data/serviceProfiles';
import { SCORE_CRITERIA_BY_ID } from '../data/cbatScoreRequirements';

const STALE_ASSESSMENT_DAYS = 14;
// Used only when no role/requirement is selected — a rough midpoint on the
// 1-9 stanine scale, not a published passing threshold for any role.
export const GENERIC_WEAK_STANINE = 5;

export const RECOMMENDATION_PRIORITY_LABELS = {
  high: 'High priority',
  medium: 'Priority',
  reassess: 'Reassess',
  untested: 'Not yet assessed'
};

// A rough, deliberately simple mapping from a time budget to how many
// recommended modules can fit in it -- not a per-module duration model
// (actual module lengths vary too much for that to be honest), just enough
// to turn the flat recommendation list into a "today's session" without
// inventing false precision.
export const SESSION_LENGTH_MINUTES_OPTIONS = [10, 20, 30];
const MINUTES_PER_RECOMMENDED_MODULE = 10;
export const moduleCountForSessionLength = (minutes) => (
  Math.max(1, Math.round(minutes / MINUTES_PER_RECOMMENDED_MODULE))
);

export const requirementForModule = (criterion, moduleId) => {
  if (!criterion) return null;
  let required = null;
  criterion.domains.forEach((domain) => {
    domain.modules.forEach((ref) => {
      if (ref.id === moduleId) required = required === null ? domain.required : Math.max(required, domain.required);
    });
  });
  return required;
};

export const buildTrainingRecommendations = ({
  serviceProfileId,
  targetRoleId,
  moduleScores,
  limit = 5,
  now = Date.now()
}) => {
  const modules = getServiceProfileModules(serviceProfileId);
  const criterion = targetRoleId ? SCORE_CRITERIA_BY_ID[targetRoleId] : null;

  const entries = modules.map((module) => {
    const score = moduleScores[module.id];
    const required = requirementForModule(criterion, module.id);

    if (!score || !Number.isFinite(score.stanine)) {
      // Never assessed -- surfaced as low-priority filler (ranked behind
      // every real weak/stale finding below) so a session always has
      // something to offer instead of only ever re-sending a candidate back
      // to the same already-known weak module.
      return {
        moduleId: module.id,
        module,
        stanine: null,
        confidence: null,
        required,
        gap: 0,
        daysSinceAssessed: null,
        priority: 'untested',
        reason: 'No attempts recorded yet'
      };
    }

    const daysSinceAssessed = Math.max(0, Math.floor((now - new Date(score.latestTimestamp).getTime()) / 86400000));
    const isBelow = required !== null ? score.stanine < required : score.stanine < GENERIC_WEAK_STANINE;
    const gap = required !== null ? required - score.stanine : Math.max(0, GENERIC_WEAK_STANINE - score.stanine);
    const isStale = !isBelow && daysSinceAssessed >= STALE_ASSESSMENT_DAYS;

    if (!isBelow && !isStale) return null;

    return {
      moduleId: module.id,
      module,
      stanine: score.stanine,
      confidence: score.confidence,
      required,
      gap,
      daysSinceAssessed,
      priority: isBelow ? (gap >= 2 ? 'high' : 'medium') : 'reassess',
      reason: isBelow
        ? (required !== null ? `${criterion.shortName || criterion.roleName} target: ${required}` : 'Below Academy training average')
        : `Last assessed ${daysSinceAssessed} day${daysSinceAssessed === 1 ? '' : 's'} ago`
    };
  }).filter(Boolean);

  const priorityRank = { high: 0, medium: 1, reassess: 2, untested: 3 };
  entries.sort((a, b) => (
    priorityRank[a.priority] - priorityRank[b.priority] || b.gap - a.gap
  ));

  return entries.slice(0, limit);
};
